// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import dayjs, { Dayjs } from "dayjs";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface ChartProps {
  data: any;
  from: Dayjs;
  to: Dayjs;
}

const CSLineChart: React.FC<ChartProps> = ({ data, from, to }) => {
  const chartRef = useRef(null);

  const chartData = {
    labels: data.map((entry: any) => entry.yearAndMonth),
    datasets: [
      {
        label: "Incident Cases",
        data: data.map((entry: any) => entry.incidentCount),
        borderColor: "#ff7300",
        backgroundColor: "#ff7300",
        tension: 0,
        fill: false,
        borderWidth: 1
      },
      {
        label: "Query Cases",
        data: data.map((entry: any) => entry.queryCount),
        borderColor: "#25AAE1",
        backgroundColor: "#25AAE1",
        tension: 0,
        fill: false,
        borderWidth: 1
      },
    ],
  };

  const maxCount = Math.max(
    ...data.flatMap((entry: any) => [entry.incidentCount, entry.queryCount])
  );

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        font: {
          size: 16,
        },
        align: "start" as const,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Months",
        },
      },
      y: {
        title: {
          display: true,
          text: "Number of Cases",
        },
        ticks: {
          stepSize: 1,
          callback: function (tickValue: string | number) {
            return Number.isInteger(tickValue) ? tickValue : null;
          },
        },
        suggestedMax: Math.ceil(maxCount + 1),
      },
    },
  };

  const hasData = chartData.datasets.some((dataset) =>
    dataset.data.some((value: number) => value > 0)
  );

  return (
    <div style={{ width: "100%", height: "400px" }}>
      {hasData ? (
        <Line ref={chartRef} data={chartData} options={chartOptions} />
      ) : (
        <p style={{ textAlign: "center" }}>No data available for case volume by months.</p>
      )}
    </div>
  );
};

export default CSLineChart;
